import { useMemo } from 'react'

import {
    useDayAheadPrices,
    UseDayAheadPricesOptions,
    UseDayAheadPricesResult,
} from './useDayAheadPrices'
import { TimePeriodResolution } from '../../utils/timePeriod'
import { meanAggregateDayAheadPrices } from './meanAggregate'

export interface UseMeanAggregatedDayAheadPricesOptions extends UseDayAheadPricesOptions {
    /**
     * Resolution to aggregate by.
     * 
     * E.g. 'hour' will aggregate four 15 minute periods to one 1 hour period.
     */
    targetResolution: TimePeriodResolution
}

/**
 * Fetch day-ahead prices aggregated to a lower resolution.
 * 
 * Only for downsampling the original 15 minute resolution, can not upsample.
 */
export const useMeanAggregatedDayAheadPrices = (
    { targetResolution, ...opts }: UseMeanAggregatedDayAheadPricesOptions
): UseDayAheadPricesResult => {
    const { isLoading, dayAheadPrices } = useDayAheadPrices(opts)

    const meanAggregatedPrices = useMemo(() => {
        if (!dayAheadPrices) {
            return []
        }
        return meanAggregateDayAheadPrices(dayAheadPrices, targetResolution)
    }, [dayAheadPrices, targetResolution])

    return {
        isLoading,
        dayAheadPrices: meanAggregatedPrices,
    }
}
